import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPhone, usePublicSettings } from '@/layouts/PublicShell';
import { PageHero } from './shared';

interface Entry {
  question: string;
  answer: string;
}

const GROUPS: Array<{ heading: string; entries: Entry[] }> = [
  {
    heading: 'The interest-free part',
    entries: [
      {
        question: 'Is it really interest-free, or is there a fee somewhere?',
        answer:
          'Really interest-free, and no fee. Your total repayable equals the amount you borrowed. There is no processing fee, no service charge, no insurance premium and no late penalty. This is not a promotional rate — the system has no field anywhere that could record such a charge against your loan.',
      },
      {
        question: 'How is the scheme funded, then?',
        answer:
          'The money is provided by the Aliu Abdulkabir Foundation, and UNIFEMGA administers the scheme for its members. Neither takes anything from borrowers. That is what qard hasan means: the lender takes no increase.',
      },
      {
        question: 'What happens if I pay late?',
        answer:
          'Nothing is added to what you owe. You will be telephoned, and if you are in genuine difficulty the repayment plan can be discussed. What is at risk is future access to borrowing, not the size of your balance.',
      },
      {
        question: 'Can I repay early?',
        answer:
          'Yes, at any time, and there is no charge for doing so. Since nothing accrues over time, repaying early simply clears the balance sooner.',
      },
    ],
  },
  {
    heading: 'Applying',
    entries: [
      {
        question: 'Who can borrow?',
        answer:
          'Financial members of UNIFEMGA in good standing, with verified KYC, a Nigerian bank account in their own name, and no loan currently in arrears.',
      },
      {
        question: 'What do I need before I start an application?',
        answer:
          'Your membership number, your verified KYC, a bank account for the payout, and the contact details of your guarantors — full name, telephone number, address and their relationship to you.',
      },
      {
        question: 'How much can I borrow, and over how long?',
        answer:
          'That depends on the loan product. Each one lists its maximum amount, its range of repayment periods and how many guarantors it needs. You can see them all on the loan products page.',
      },
      {
        question: 'How long does approval take?',
        answer:
          'The step that usually determines the timing is reaching your guarantors by telephone. Speaking to them beforehand, and giving numbers they actually answer, is the single biggest thing you can do to speed it up.',
      },
      {
        question: 'Can I be approved for less than I asked for?',
        answer:
          'Yes. A reviewer may approve a smaller amount or a different repayment period. If so, the approved figure and the reason are shown on your application.',
      },
    ],
  },
  {
    heading: 'Guarantors',
    entries: [
      {
        question: 'Does my guarantor need an account on this platform?',
        answer:
          'No. You enter their details on the application form and an officer telephones them. They never have to register, log in, or use this website at all.',
      },
      {
        question: 'What exactly is my guarantor agreeing to?',
        answer:
          'To vouch for you, and to be contacted if you fall behind. Because there is no interest, the exposure never grows: it can never exceed the amount you originally borrowed.',
      },
      {
        question: 'Should I speak to my guarantors before I apply?',
        answer:
          'Yes — please do. The application assumes you already have. An officer will call each of them to confirm, and a guarantor who is surprised by that call will usually decline.',
      },
      {
        question: 'What if a guarantor cannot be reached or declines?',
        answer:
          'The application waits. Telephone UNIFEMGA with a better number, or name someone else. Nothing can be approved until the required number of guarantors has been confirmed by telephone.',
      },
      {
        question: 'Can I guarantee more than one person?',
        answer:
          'Yes, though your total exposure across everyone you are standing for is visible to reviewers and is taken into account.',
      },
    ],
  },
  {
    heading: 'Repayment and your account',
    entries: [
      {
        question: 'How do repayments work?',
        answer:
          'Your loan is divided into equal repayments across the period you chose. The schedule appears in your portal the moment the loan is disbursed, and every naira you pay reduces your balance because nothing is being added to it.',
      },
      {
        question: 'What is the difference between "My applications" and "My loans"?',
        answer:
          'An application is a request, and it ends at a decision. A loan is money you owe, and it only exists after an application is approved. An application that was declined never becomes a loan.',
      },
      {
        question: 'Is my personal information safe?',
        answer:
          'Identification numbers are encrypted in the database, passwords are hashed, and every administrative action is written to an audit trail that nothing in the system can edit or delete. Read the privacy policy for the detail.',
      },
      {
        question: 'What happens if I default?',
        answer:
          'Your balance stays exactly what it was — a default cannot make you owe more. What changes is access: borrowing is suspended for a period, and the association will contact you and your guarantors to agree how the outstanding amount is settled.',
      },
    ],
  },
];

/**
 * Frequently asked questions.
 *
 * Grouped rather than one long accordion, because the questions divide cleanly
 * into "is this really free", "how do I apply", "what am I asking my guarantor
 * for" and "what happens after" — and people arrive knowing which of those they
 * came for.
 */
export default function Faq() {
  const org = usePublicSettings();
  const [open, setOpen] = useState<string | null>(GROUPS[0]!.entries[0]!.question);

  return (
    <>
      <PageHero
        eyebrow="FAQs"
        title="Questions members actually ask."
        description="If yours is not here, telephone the secretariat — someone will answer it properly."
      />

      <section className="py-14 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-3xl space-y-12">
            {GROUPS.map(({ heading, entries }) => (
              <div key={heading}>
                <h2 className="mb-5 font-heading text-xl font-bold tracking-tight text-foreground md:text-2xl">
                  {heading}
                </h2>

                <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                  {entries.map(({ question, answer }) => {
                    const isOpen = open === question;
                    return (
                      <div key={question}>
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : question)}
                          aria-expanded={isOpen}
                          className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/50 sm:px-7 sm:py-5"
                        >
                          <span className="font-semibold text-foreground">{question}</span>
                          <ChevronDown
                            size={18}
                            className={cn(
                              'mt-0.5 shrink-0 text-foreground-secondary transition-transform duration-200',
                              isOpen && 'rotate-180 text-primary',
                            )}
                          />
                        </button>
                        {isOpen && (
                          <p className="px-5 pb-5 text-[15px] leading-relaxed text-foreground-secondary sm:px-7 sm:pb-6">
                            {answer}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="rounded-2xl bg-foreground p-7 text-background sm:p-9">
              <h2 className="font-heading text-xl font-bold">Still unsure?</h2>
              <p className="mt-2 text-sm leading-relaxed text-background/60">
                Nothing here replaces a conversation. Call the secretariat and ask.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <a
                  href={`tel:${org['org.phone']}`}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 font-bold text-primary-foreground"
                >
                  <Phone size={16} /> {formatPhone(org['org.phone'])}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-background/20 px-6 py-3.5 font-bold text-background transition-colors hover:bg-background/10"
                >
                  All ways to reach us <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
